import { GPSTrack } from '@nx-smartmonkey/shared/domain';

import { Driver } from '../../../../domain/driver/Driver';
import { DriverStatus } from '../../../../domain/driver/DriverStatus';
import { GeoFence } from '../../../../domain/geoFence/GeoFence';
import { PlanFlat } from '../../../../domain/plan/PlanFlat';
import { Stop } from '../../../../domain/stop/Stop';
import { Task } from '../../../../domain/task/Task';
import { MapFilters, OptimizationData } from './reducer';
import { DriversTableColumn } from './tableColumns/driversTableColumns';
import { StopsTableColumn } from './tableColumns/stopsTableColumns';
import { TasksTableColumn } from './tableColumns/tasksTableColumns';

export enum PlanDetailsActionType {
  SET_ACTIVE_PLAN = `SET_ACTIVE_PLAN`,
  SET_ACTIVE_PLAN_STOPS = `SET_ACTIVE_PLAN_STOPS`,
  ON_SELECT_STOP = `ON_SELECT_STOP`,
  SET_ACTIVE_PLAN_SELECTED_STOPS = `SET_ACTIVE_PLAN_SELECTED_STOPS`,
  CREATE_PLAN_STOP = `CREATE_PLAN_STOP`,
  UPDATE_PLAN_STOP = `UPDATE_PLAN_STOP`,
  RESCHEDULE_PLAN_STOP = `RESCHEDULE_PLAN_STOP`,
  DELETE_PLAN_STOP = `DELETE_PLAN_STOP`,
  SET_ACTIVE_PLAN_DRIVERS = `SET_ACTIVE_PLAN_DRIVERS`,
  SET_ACTIVE_PLAN_SELECTED_DRIVERS = `SET_ACTIVE_PLAN_SELECTED_DRIVERS`,
  SET_ACTIVE_PLAN_GEO_FENCES = `SET_ACTIVE_PLAN_GEO_FENCES`,
  CREATE_PLAN_DRIVER = `CREATE_PLAN_DRIVER`,
  UPDATE_PLAN_DRIVER = `UPDATE_PLAN_DRIVER`,
  DELETE_PLAN_DRIVER = `DELETE_PLAN_DRIVER`,
  SET_ACTIVE_PLAN_TASKS = `SET_ACTIVE_PLAN_TASKS`,
  SET_ACTIVE_PLAN_SELECTED_TASKS = `SET_ACTIVE_PLAN_SELECTED_TASKS`,
  ON_SELECT_TASK = `ON_SELECT_TASK`,
  PLAN_STOP_RESCHEDULED = `PLAN_STOP_RESCHEDULED`,
  UPDATE_PLAN_DRIVER_LAST_POSITION = `UPDATE_PLAN_DRIVER_LAST_POSITION`,
  UPDATE_PLAN_DRIVER_STATUS = `UPDATE_PLAN_DRIVER_STATUS`,
  SET_ACTIVE_PLAN_MAP_FILTERS = `SET_ACTIVE_PLAN_MAP_FILTERS`,
  SET_ACTIVE_PLAN_OPTIMIZATION_DATA = `SET_ACTIVE_PLAN_OPTIMIZATION_DATA`,
  SET_ACTIVE_PLAN_EDITING_GEO_FENCE = `SET_ACTIVE_PLAN_EDITING_GEO_FENCE`,
  SET_STOPS_TABLE_COLUMNS = `SET_STOPS_TABLE_COLUMNS`,
  SET_DRIVERS_TABLE_COLUMNS = `SET_DRIVERS_TABLE_COLUMNS`,
  SET_TASKS_TABLE_COLUMNS = `SET_TASKS_TABLE_COLUMNS`,
}

export interface SetActivePlanAction {
  type: PlanDetailsActionType.SET_ACTIVE_PLAN;
  payload: {
    activePlan?: PlanFlat;
  };
}

export interface SetActivePlanDriversAction {
  type: PlanDetailsActionType.SET_ACTIVE_PLAN_DRIVERS;
  payload: {
    planDrivers: Array<Driver>;
  };
}

export interface SetActivePlanStopsAction {
  type: PlanDetailsActionType.SET_ACTIVE_PLAN_STOPS;
  payload: {
    planStops: Array<Stop>;
  };
}
export interface OnSelectStopAction {
  type: PlanDetailsActionType.ON_SELECT_STOP;
  payload: {
    stopId: string;
    withControlKey?: boolean;
  };
}

export interface SetActivePlanSelectedStopsAction {
  type: PlanDetailsActionType.SET_ACTIVE_PLAN_SELECTED_STOPS;
  payload: {
    selectedStops: Array<string>;
  };
}

export interface SetActivePlanSelectedDriversAction {
  type: PlanDetailsActionType.SET_ACTIVE_PLAN_SELECTED_DRIVERS;
  payload: {
    selectedDrivers: Array<string>;
  };
}

export interface SetActivePlanSelectedTasksAction {
  type: PlanDetailsActionType.SET_ACTIVE_PLAN_SELECTED_TASKS;
  payload: {
    selectedTasks: Array<string>;
  };
}

export interface SetActivePlanGeoFencesAction {
  type: PlanDetailsActionType.SET_ACTIVE_PLAN_GEO_FENCES;
  payload: {
    planGeoFences: Array<GeoFence>;
  };
}

export interface CreatePlanStopAction {
  type: PlanDetailsActionType.CREATE_PLAN_STOP;
  payload: {
    stop: Stop;
  };
}

export interface UpdatePlanStopAction {
  type: PlanDetailsActionType.UPDATE_PLAN_STOP;
  payload: {
    stop: Stop;
  };
}

export interface ReschedulePlanStopAction {
  type: PlanDetailsActionType.RESCHEDULE_PLAN_STOP;
  payload: {
    stop: Stop;
  };
}

export interface DeletePlanStopAction {
  type: PlanDetailsActionType.DELETE_PLAN_STOP;
  payload: {
    stop: Stop;
  };
}

export interface CreatePlanDriverAction {
  type: PlanDetailsActionType.CREATE_PLAN_DRIVER;
  payload: {
    driver: Driver;
  };
}

export interface UpdatePlanDriverAction {
  type: PlanDetailsActionType.UPDATE_PLAN_DRIVER;
  payload: {
    driver: Driver;
  };
}

export interface PlanStopRescheduledAction {
  type: PlanDetailsActionType.PLAN_STOP_RESCHEDULED;
  payload: {
    stop: Stop;
  };
}

export interface UpdatePlanDriverLastPositionAction {
  type: PlanDetailsActionType.UPDATE_PLAN_DRIVER_LAST_POSITION;
  payload: {
    driverId: string;
    lastPosition?: GPSTrack;
  };
}

export interface UpdatePlanDriverStatusAction {
  type: PlanDetailsActionType.UPDATE_PLAN_DRIVER_STATUS;
  payload: {
    status: DriverStatus;
    driverId: string;
  };
}

export interface DeletePlanDriverAction {
  type: PlanDetailsActionType.DELETE_PLAN_DRIVER;
  payload: {
    driver: Driver;
  };
}

export interface SetActivePlanTasksAction {
  type: PlanDetailsActionType.SET_ACTIVE_PLAN_TASKS;
  payload: {
    tasks: Array<Task>;
  };
}
export interface OnSelectTasksAction {
  type: PlanDetailsActionType.ON_SELECT_TASK;
  payload: {
    taskId: string;
    withControlKey?: boolean;
  };
}

export interface SetActivePlanMapFiltersAction {
  type: PlanDetailsActionType.SET_ACTIVE_PLAN_MAP_FILTERS;
  payload: {
    mapFilters: MapFilters;
  };
}

export interface SetActivePlanOptimizationDataAction {
  type: PlanDetailsActionType.SET_ACTIVE_PLAN_OPTIMIZATION_DATA;
  payload: OptimizationData;
}

export interface SetActivePlanEditingGeoFenceAction {
  type: PlanDetailsActionType.SET_ACTIVE_PLAN_EDITING_GEO_FENCE;
  payload: {
    editingGeoFence?: GeoFence;
  };
}

export interface SetStopsTableColumnsAction {
  type: PlanDetailsActionType.SET_STOPS_TABLE_COLUMNS;
  payload: Array<StopsTableColumn>;
}

export interface SetDriversTableColumnsAction {
  type: PlanDetailsActionType.SET_DRIVERS_TABLE_COLUMNS;
  payload: Array<DriversTableColumn>;
}

export interface SetTasksTableColumnsAction {
  type: PlanDetailsActionType.SET_TASKS_TABLE_COLUMNS;
  payload: Array<TasksTableColumn>;
}

export type PlanDetailsActions =
  | SetActivePlanAction
  | SetActivePlanDriversAction
  | SetActivePlanStopsAction
  | OnSelectStopAction
  | SetActivePlanSelectedStopsAction
  | SetActivePlanSelectedDriversAction
  | SetActivePlanSelectedTasksAction
  | SetActivePlanGeoFencesAction
  | CreatePlanStopAction
  | UpdatePlanStopAction
  | ReschedulePlanStopAction
  | DeletePlanStopAction
  | CreatePlanDriverAction
  | UpdatePlanDriverAction
  | SetActivePlanTasksAction
  | OnSelectTasksAction
  | PlanStopRescheduledAction
  | UpdatePlanDriverLastPositionAction
  | UpdatePlanDriverStatusAction
  | DeletePlanDriverAction
  | SetActivePlanMapFiltersAction
  | SetActivePlanOptimizationDataAction
  | SetActivePlanEditingGeoFenceAction
  | SetStopsTableColumnsAction
  | SetDriversTableColumnsAction
  | SetTasksTableColumnsAction;
